/**
 * YabanciKatilimciParcalari.jsx — Yabancı Katılımcı (yabancı firma, KATILIMCI rolü) için SADE personel görünümü
 * (backlog #511, Deniz 2026-10-19: "important guests, they like simplicity").
 *
 * Yabancı Katılımcı'dan yalnızca iki kişisel şey istenir: GDPR (KVKK) onayı ve pasaport. Eski (Yerli) görünümdeki SGK / İSG /
 * Sağlık / Mesleki Eğitim kareleri, meslekler ve belge havuzu bu kullanıcıya hiç gösterilmez. Yabancı Dekoratör ve Yerli
 * Katılımcı eski sayfalarını görmeye devam eder: bu dosya yalnızca etkin arayüz seti `YABANCI_KATILIMCI` iken çağrılır.
 *
 * Kullanan yerler:
 *   · Components/Kisi/YabanciKatilimciKarti.jsx    — liste satırı (Pasaport + GDPR (SMS) kareleri: `YabanciKatilimciBelgeKutulari`)
 *   · pages/Firma/FirmaPersonelDetailPage.jsx      — Belgeler sekmesinde iki kart (`GdprKarti`, `PasaportKarti`)
 *
 * GDPR kutusu yalnızca DURUMU gösterir: KVKK kişinin kendi rızasıdır, firma yükleyemez (backend #249 → 403, #252).
 * Pasaport kutusu gerçektir: tıklayınca KimlikPasaportModal açılır; pasaport dosyası `POST /personnel/dosyaekle/{id}` (file_type 90,
 * personelBelgeService / kisiAdminService.uploadKimlikBelgesi), numara ve ülke `PUT /personnel/{id}/pasaport`. Kimlik doğrulamayı
 * (`PATCH /{id}/kimlik-dogrulama`) yalnızca FDS personeli verir.
 */
import React from "react";
import { Button, Card, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { StatusTile, STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import KisiDosyaLink from "./KisiDosyaLink";

/** Backend'in arayüz seti kodu (PortalConstant): yabancı + KATILIMCI. */
export const YABANCI_KATILIMCI_KODU = "YABANCI_KATILIMCI";

/**
 * Girişin ETKİN arayüz seti Yabancı Katılımcı mı? Bir firma iki set taşıyabilir (katılımcı + kendi dekoratörü); sayfa etkin sete bakar.
 * #704 (2026-11-03): bu soru artık yalnızca BELGELER içindir (GDPR + Pasaport, İmza Sirküsü + İSG Taahhütnamesi, İSG yetkilisi yok): Sade panel
 * modunda Yerli bir firma sade DÜZENİ görür ama belgeleri kendi uyruğununkiler kalır. Düzen services/sadePanel.sadePanelMi'ye bakar.
 */
export const yabanciKatilimciMi = (portal) => portal?.aktif?.kod === YABANCI_KATILIMCI_KODU;

// ─── Simgeler ────────────────────────────────────────────────────────────────────────

/** KVKK karesinin içi: simge yok, "GDPR" yazar. StatusTile'ın `icon` sözleşmesi: <Icon size color />. */
export function GdprIkonu({ color = "#fff" }) {
  return (
    <span style={{ color, fontSize: 10, fontWeight: 700, letterSpacing: "0.02em", lineHeight: 1 }} aria-hidden="true">GDPR</span>
  );
}

/** Pasaport simgesi: küre arması olan, kapağı yuvarlak dikdörtgen bir cüzdan (lucide'de pasaport yok). */
export function PasaportIkonu({ size = 18, color = "currentColor" }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    >
      <rect x="4.5" y="2" width="15" height="20" rx="2.5" />
      <circle cx="12" cy="10" r="3.5" />
      <ellipse cx="12" cy="10" rx="1.5" ry="3.5" />
      <path d="M8.5 10h7" />
      <path d="M8.5 18h7" />
    </svg>
  );
}

// ─── Durumlar ────────────────────────────────────────────────────────────────────────

const RENK = {
  onayli: STATUS_HEX.success,
  dogrulandi: STATUS_HEX.success,
  incelemede: STATUS_HEX.warning,
  masada: STATUS_HEX.info,
  reddedildi: STATUS_HEX.danger,
  yok: NEUTRAL_GRAY,
};

/** Bir durum anahtarının kare / rozet rengi (STATUS_HEX; bu temada rozet sınıfları güvenilmez). */
export const durumRengi = (durum) => RENK[durum] ?? NEUTRAL_GRAY;

/**
 * GDPR (KVKK) durumu: 'onayli' | 'incelemede' | 'reddedildi' | 'yok'.
 * KVKK'nın üç kaynağından biri yeterli: onaylı belge ya da kişinin kendi dijital onayı (`kvkkDijitalOnay.tamam`, backend #139).
 * `dto`: kadro satırının ayrıntısı ya da kişi detayı (ikisi de `documents{}` ve `kvkkDijitalOnay` taşır).
 */
export function gdprDurumu(dto) {
  const status = dto?.documents?.kvkk?.status ?? null;
  if (status === 1 || dto?.kvkkDijitalOnay?.tamam) return "onayli";
  if (status === 2) return "reddedildi";
  if (status === 0) return "incelemede";
  return "yok";
}

/**
 * Pasaport durumu: 'dogrulandi' | 'masada' | 'incelemede' | 'reddedildi' | 'yok'.
 * #719: kimlik durumu 3 = SAKA belgeyi masada onayladı, kişi girişte doğrulanacak → 'masada' (mavi). WAS: 'incelemede' (sarı).
 * Renk iki şeyin birleşimi: FDS personelinin kimlik doğrulaması (`kimlikDogrulama.durum`: 1 doğrulandı · 2 reddedildi) ve yüklenen
 * pasaportun inceleme durumu (`documents.kimlikFotografi.status`). Doğrulandıysa yeşil; doğrulama ya da belge reddedildiyse kırmızı;
 * bir dosya yüklenmiş ama doğrulama henüz yoksa sarı (FDS bekliyor); hiçbir şey yoksa gri.
 * `dogrulamaDurum`: ince kadro satırının `kimlikDogrulamaDurum`u (ayrıntı henüz okunmadıysa yedek).
 */
export function pasaportDurumu(dto, dogrulamaDurum = null) {
  const dogrulama = dto?.kimlikDogrulama?.durum ?? dogrulamaDurum ?? null;
  const belge = dto?.documents?.kimlikFotografi?.status ?? null;
  if (dogrulama === 1) return "dogrulandi";
  if (dogrulama === 2 || belge === 2) return "reddedildi";
  if (dogrulama === 3) return "masada";
  if (belge != null) return "incelemede";
  return "yok";
}

// ─── Liste satırı: iki kutu ──────────────────────────────────────────────────────────

/**
 * Kadro satırının belge alanı: Pasaport + GDPR (SMS), her karenin altında adı yazılı (Deniz'in çizimi, 2026-10-21). GDPR kutusu YALNIZCA
 * durumu gösterir (şirket çalışanı adına GDPR yükleyemez); pasaport kutusu `onPasaport` verilirse tıklanır (yalnızca okuma → tıklanamaz).
 * `dto` yoksa (kişinin ayrıntısı okunmayan kadro dışı satır) GDPR bilinmez → çizilmez, pasaport satırın kimlik doğrulamasından renklenir.
 * WAS (until 2026-10-21): GDPR · Pasaport sırasıyla, kutunun yanında durum yazısı ("Alındı" / "Doğrulandı"); durum artık yalnızca renkte ve ipucunda.
 */
export function YabanciKatilimciBelgeKutulari({ dto, dogrulamaDurum = null, onPasaport }) {
  const { t } = useTranslation("components");
  const pasaport = pasaportDurumu(dto, dogrulamaDurum);
  const gdpr = dto ? gdprDurumu(dto) : null;
  return (
    <div className="d-flex align-items-start gap-3">
      <StatusTile
        icon={PasaportIkonu} background={durumRengi(pasaport)} label={t(`yabanciKatilimci.pasaportIpucu.${pasaport}`)}
        caption={t("yabanciKatilimci.pasaport")} onClick={onPasaport} tour="personel-pasaport"
      />
      {gdpr && (
        <StatusTile
          icon={GdprIkonu} background={durumRengi(gdpr)} label={`${t(`yabanciKatilimci.gdprIpucu.${gdpr}`)} — ${t("yabanciKatilimci.gdprBilgi")}`}
          caption={t("yabanciKatilimci.gdprSms")} tour="personel-kvkk"
        />
      )}
    </div>
  );
}

// ─── Kişi detayı: iki kart ───────────────────────────────────────────────────────────

// Rozetler düz `span.badge` + satır içi renk: reactstrap <Badge> `bg-secondary !important` sınıfı bu temada satır içi rengi eziyor (hep yeşil-mavi).
function KartIkonu({ tile, children }) {
  return (
    <div
      className="d-inline-flex align-items-center justify-content-center rounded-3 mb-2"
      style={{ width: 44, height: 44, background: tile }}
    >
      {children}
    </div>
  );
}

/** Detay sayfası GDPR kartı: durum + neden yükleme yok. Kimse yükleyemez; kişi kendi girişinden verir ya da aslını FDS'ye teslim eder. */
export function GdprKarti({ person }) {
  const { t } = useTranslation("components");
  const durum = gdprDurumu(person);
  const renk = durumRengi(durum);
  return (
    <Card id="belge-card-kvkk" className="h-100">
      <CardBody className="text-center">
        <KartIkonu tile={renk}><GdprIkonu /></KartIkonu>
        <div className="small mb-2">GDPR</div>
        <span className="badge text-white" style={{ backgroundColor: renk }}>{t(`yabanciKatilimci.gdprIpucu.${durum}`)}</span>
        <div className="small text-muted mt-2">{t("yabanciKatilimci.gdprBilgi")}</div>
      </CardBody>
    </Card>
  );
}

/**
 * Detay sayfası Pasaport kartı: durum, varsa numara ve dosya bağlantısı, "Yükle / düzenle" (KimlikPasaportModal'ı açar).
 * `salt`: ilişki sonlandırılmış → yazma düğmesi yok (yazma uçları 403 verirdi).
 */
export function PasaportKarti({ person, salt = false, onDuzenle }) {
  const { t } = useTranslation("components");
  const durum = pasaportDurumu(person);
  const renk = durumRengi(durum);
  const belge = person?.documents?.kimlikFotografi;
  const no = person?.kimlikDogrulama?.pasaportNo;
  return (
    <Card id="belge-card-kimlikFotografi" className="h-100">
      <CardBody className="text-center">
        <KartIkonu tile={renk}><PasaportIkonu size={20} color="#fff" /></KartIkonu>
        <div className="small mb-2">{t("yabanciKatilimci.pasaport")}</div>
        <span className="badge text-white" style={{ backgroundColor: renk }}>{t(`yabanciKatilimci.pasaportIpucu.${durum}`)}</span>
        {no && <div className="small text-muted mt-2">{no}</div>}
        {person?.id != null && belge?.fileId != null && (
          <div className="mt-2"><KisiDosyaLink personId={person.id} fileId={belge.fileId} label={t("yabanciKatilimci.ac")} /></div>
        )}
        <div className="small text-muted mt-2">{t("yabanciKatilimci.pasaportBilgi")}</div>
        {!salt && durum !== "dogrulandi" && (
          <div className="mt-2">
            <Button color="primary" outline size="sm" onClick={onDuzenle}>
              {belge?.fileId != null ? t("yabanciKatilimci.pasaportDuzenle") : t("yabanciKatilimci.pasaportYukle")}
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
